// ---------------------------------------------------------------------------
// Client for AEMET's ROCIO_IBEB grid (5km, daily precipitation + tmin/tmax,
// 1951 through each variable's own last date, Peninsular Spain + Balearic
// Islands) — a third alternative to AEMET's own per-station data (aemet.ts)
// for filling in a sub-basin's precipitation/temperature/ETP series, next to
// "nearest station" and IDW.
//
// Unlike AEMET's live REST API, ROCIO is only distributed as a handful of
// huge static files (no per-cell/per-range download endpoint at all — see
// scripts/prepare-rocio-grid.cjs and scripts/prepare-rocio-temperature.cjs's
// header comments for the real numbers and the reasoning behind
// pre-splitting into one small file per grid cell, per variable). This
// module never talks to AEMET directly: grid coordinates come from
// public/rocio/{manifest,coords}.json (committed, small, static — same
// pattern as public/subcuencas-miteco/), and cell VALUES come from
// `/api/rocio/cells` (functions/api/rocio/cells.ts), which reads
// pre-processed per-cell files from Cloudflare R2 — never AEMET, never the
// original source files, at request time.
//
// CANDIDATE SELECTION: unlike station methods (which always approximate from
// the sub-basin's centroid, since a "station footprint" isn't a real
// concept), ROCIO cells have a real, known 5km footprint — `cellsInPolygon`
// selects EVERY cell whose center actually falls inside the sub-basin's own
// polygon (point-in-polygon, gisLayer.ts's `pointInPolygon`), not just the
// nearest few to its centroid. `nearestGridCells` (centroid + radius) still
// exists as the fallback for when no geometry is available, or the sub-basin
// is smaller than a single 5km cell (see aemetJobStore.ts's `resolveCandidates`).
//
// AGGREGATION (see `buildRocioSeries`/`buildRocioEtoSeries` below): once the
// cell set is known, precipitation takes the MEAN of every cell's value
// (mm is already an areal depth — summing several co-located cells would
// multiply the real rainfall by however many cells the sub-basin happens to
// span, not describe it; fixed 2026-08-26 after a real Turia-basin import
// showed multi-cell sub-basins with a mean annual precipitation upward of
// 7000mm, ~14x a same-climate single-cell sub-basin's ~500mm — the
// `"sum"` this used to be was documented here as "a deliberate, explicit
// product decision", but produced a physically impossible depth, not a
// real design tradeoff). Temperature takes the MINIMUM (tmin) across cells,
// and ETP is Hargreaves fed by the grid's own MAX(tmax)/MIN(tmin) — those
// two are genuinely deliberate (an extreme, not a depth), not IDW-style
// distance blending (which doesn't apply here: every selected cell is
// equally "inside" the sub-basin, there's no meaningful notion of one being
// more central than another for this purpose).
//
// LOCAL DEV: there is no way to test against real R2 data without
// uploading the processed cell files there first (a manual step, see the
// scripts' header comments) — so in dev mode (`import.meta.env.DEV`) this
// module never calls `/api/rocio/cells` at all, it generates
// plausible-looking mock daily values instead (see `mockDailyMm`/
// `mockDailyTmin`/`mockDailyTmax` below), against the REAL grid coordinates
// (public/rocio/coords.json is small enough to just commit, so cell
// selection is realistic even with mocked values). Production (a real
// `vite build`) always calls the real endpoint — `import.meta.env.DEV` is
// statically `false` there, same guarantee aemet.ts's own dev escape hatch
// relies on.
// ---------------------------------------------------------------------------
import { hargreavesEto } from "./eto.ts";
import { haversineDistanceKm, type LatLon } from "./geo.ts";
import { addDaysIso, dayOfYear, daysBetweenIso } from "./isoDate.ts";
import { geometryBounds, pointInPolygon } from "./gisLayer.ts";
import {
  translatableFetchError,
  withTimeout,
  type AemetDailyRecord,
  type BuiltSeries,
  type DateRange,
  type FetchDailyValuesResult,
  type StationCandidate,
} from "./aemet/aemet.ts";

export interface RocioCell extends LatLon {
  id: number;
  heightM: number;
}

export interface RocioManifest {
  /** ISO yyyy-mm-dd — the grid's first available date (fixed, from the source file). */
  startDate: string;
  /** How many consecutive days the grid covers, starting at `startDate`. */
  days: number;
  /** Storage scale for the raw per-cell files (server-side only, exposed here for completeness) — NOT needed by callers of `fetchRocioValues`, which already returns real mm. */
  scale: number;
  unit: "mm";
  /** ISO yyyy-mm-dd — AEMET's OWN publish date for the source file (its HTTP `Last-Modified` header, captured by scripts/prepare-rocio-grid.cjs), NOT the date this app fetched/processed it. Shown to the user (AemetProvenanceDialog.tsx) instead of our own fetch date — "how fresh is this data" should reflect AEMET's own versioning. Optional: absent for old manifests generated before this field existed. */
  sourceUpdatedAt?: string;
}

/** `sourceByDate` label for every date the grid actually covered — mirrors `IDW_SOURCE_LABEL` in aemet.ts (never a real station code, so `AemetProvenanceDialog.tsx`'s station-name lookup falls through to showing it verbatim). */
export const ROCIO_SOURCE_LABEL = "ROCIO";

/**
 * ROCIO_IBEB's real date coverage — hardcoded to match the currently
 * committed public/rocio/manifest.json (startDate "1951-01-01", 26298 days
 * = through 2022-12-31). Used ONLY to give the "Malla ROCIO" method a
 * sensible default/limit for its own date-range picker (AemetFetchDialog.tsx/
 * AemetBulkFetchDialog.tsx) — real fetches always reflect the ACTUAL
 * manifest server-side regardless of this constant (a day outside real
 * coverage already surfaces as "sin dato", see functions/api/rocio/cells.ts),
 * so a stale value here degrades to "the UI's default/limit is a bit off",
 * never silent data loss. Update these two lines if AEMET extends the
 * grid's coverage and the manifest is regenerated — see README.md's "Datos
 * de la malla ROCIO" section for the refresh procedure.
 */
export const ROCIO_COVERAGE_START = "1951-01-01";
export const ROCIO_COVERAGE_END = "2022-12-31";

// Real incident (2026-08-25): the MITECO import wizard (Step1SubBasins.tsx)
// used to request ROCIO's FULL coverage (72 years) for every sub-basin
// regardless of how many there were — a real Duero import (1728 sub-basins)
// crashed the tab with an out-of-memory error. Each sub-basin's own
// precipitation+temperature+ETP columns cost `BYTES_PER_SUBBASIN_DAY` per
// day requested (3 series × 8 bytes, Float64Array) — `RAW_BUDGET_BYTES` is
// a fixed ceiling on subBasinCount × daysRequested, chosen so it covers the
// full 72 years for any project up to ~415 sub-basins (comfortably above
// the "small project" case, where the full record is genuinely useful) and
// scales the range down smoothly past that, rather than a hard cutoff at
// some sub-basin count — `MIN_DAYS` keeps even a very large import (a real
// Duero-scale one lands around ~17 years) from being handed a record too
// short to calibrate anything against.
const RAW_BUDGET_BYTES = 250 * 1024 * 1024;
const BYTES_PER_SUBBASIN_DAY = 24; // precipitation + temperature + ETP, 8 bytes (Float64) each.
const MIN_DAYS = Math.round(10 * 365.25);

/**
 * The ROCIO date range the MITECO import wizard should request for a
 * project with `subBasinCount` sub-basins — see the constants above for
 * the reasoning. Always ends at `ROCIO_COVERAGE_END` (the most recent
 * data), extending as far back as the budget allows, clamped to never
 * reach earlier than `ROCIO_COVERAGE_START` itself.
 */
export function rocioDateRangeForSubBasinCount(subBasinCount: number): DateRange {
  const fullDays = daysBetweenIso(ROCIO_COVERAGE_START, ROCIO_COVERAGE_END) + 1;
  const budgetDays = Math.floor(
    RAW_BUDGET_BYTES / BYTES_PER_SUBBASIN_DAY / Math.max(1, subBasinCount)
  );
  const days = Math.max(MIN_DAYS, Math.min(fullDays, budgetDays));
  if (days >= fullDays) return { start: ROCIO_COVERAGE_START, end: ROCIO_COVERAGE_END };
  return { start: addDaysIso(ROCIO_COVERAGE_END, -(days - 1)), end: ROCIO_COVERAGE_END };
}

let gridPromise: Promise<{ manifest: RocioManifest; cells: RocioCell[] }> | null = null;

/**
 * Grid metadata (manifest + every cell's coordinates) — cached in memory for
 * the session, same pattern as `fetchStationInventory` (aemet.ts): a timeout
 * (see `withTimeout`'s own comment — a stalled request here would otherwise
 * hang FOREVER and, since this is the very first thing a "rocio"-method job
 * awaits, permanently stick it in "loading-stations"), and the cache resets
 * to `null` on failure so the NEXT call actually retries against the network
 * instead of replaying the same rejected/failed promise for the rest of the
 * session. Small enough (a few hundred KB) to fetch as one plain static
 * request, no Function/R2 round trip needed just for this.
 */
export function fetchRocioGrid(): Promise<{ manifest: RocioManifest; cells: RocioCell[] }> {
  gridPromise ??= Promise.all([
    fetch("/rocio/manifest.json", { signal: withTimeout(undefined) }).then(
      (r) => r.json() as Promise<RocioManifest>
    ),
    fetch("/rocio/coords.json", { signal: withTimeout(undefined) }).then(
      (r) => r.json() as Promise<[number, number, number][]>
    ),
  ])
    .then(([manifest, coords]) => ({
      manifest,
      cells: coords.map(([lon, lat, heightM], i) => ({ id: i + 1, lon, lat, heightM })),
    }))
    .catch((err) => {
      gridPromise = null;
      throw err;
    });
  return gridPromise;
}

/**
 * Closest grid cells to `point`, nearest first — same shape/defaults as
 * `nearestStations` (aemet.ts), returning `StationCandidate`-compatible
 * objects (`code` = the cell's numeric id as a string, `name` = a
 * human-readable "Celda ROCIO #<id>" label) so every existing
 * station-shaped consumer (`buildSeriesFromStations(Idw)`,
 * `AemetProvenanceDialog.tsx`'s station-name lookup, ...) works completely
 * unchanged for the ROCIO method too — a job only ever populates candidates
 * from ONE source (stations OR grid cells, never mixed), so reusing the
 * same `code` namespace can't collide within a single `byStationByDate` map.
 *
 * `limit` defaults higher than `nearestStations`' 5: a sub-basin's real
 * footprint typically spans several 5km cells, not just the one nearest its
 * centroid — using several gives IDW something real to blend, and even
 * "nearest cell" mode benefits from a couple of fallback candidates (same
 * gap-filling idea as `buildSeriesFromStations`), though gaps should be
 * rare in practice: ROCIO is already a complete interpolated grid.
 */
export function nearestGridCells(
  point: LatLon,
  cells: RocioCell[],
  opts: { maxKm?: number; limit?: number } = {}
): StationCandidate[] {
  const maxKm = opts.maxKm ?? 15;
  const limit = opts.limit ?? 12;
  return cells
    .map((c) => ({
      code: String(c.id),
      name: `Celda ROCIO #${c.id}`,
      lat: c.lat,
      lon: c.lon,
      altitudeM: c.heightM,
      distanceKm: haversineDistanceKm(point, c),
    }))
    .filter((c) => c.distanceKm <= maxKm)
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, limit);
}

/**
 * EVERY grid cell whose center falls inside `geometry` (Polygon/MultiPolygon
 * point-in-polygon test — gisLayer.ts's `pointInPolygon`), not an
 * approximation from a single point: a sub-basin's real footprint typically
 * spans several to dozens of 5km cells, and the mean/min/max aggregation this
 * method uses (`buildRocioSeries`/`buildRocioEtoSeries` below) needs the
 * actual set of cells inside that footprint, not "the nearest few to the
 * centroid" — unlike IDW/nearest-station, which blend by distance and so
 * tolerate that approximation fine.
 *
 * Returns the same `StationCandidate` shape as `nearestGridCells` for the
 * same downstream-compatibility reason (see that function's own comment).
 * `distanceKm` is measured from `referencePoint` (the sub-basin's centroid,
 * in practice) purely for display/sorting — it plays no role in how these
 * cells' values get combined (mean/min/max don't weight by distance the way
 * IDW does). Can return an empty array (a sub-basin smaller than one 5km
 * cell, with no cell center landing inside it) — see
 * `aemetJobStore.ts`'s `resolveCandidates` for the `nearestGridCells`
 * fallback in that case.
 *
 * Pre-filters by the geometry's bounding box before the real (ray-casting)
 * test: with the full grid at 16k+ cells covering all of Spain, and this
 * running once per sub-basin on a bulk import (the MITECO wizard can queue
 * over a thousand), doing the expensive O(ring vertices) test against every
 * single cell — including ones nowhere near the sub-basin — was measurable
 * main-thread blocking. The bbox check is a handful of number comparisons
 * per cell and never excludes a true match (a point inside the polygon is
 * always inside its own bounding box), so this changes nothing about the
 * result, only how many cells reach the expensive test.
 */
export function cellsInPolygon(
  cells: RocioCell[],
  geometry: GeoJSON.Geometry,
  referencePoint: LatLon
): StationCandidate[] {
  const bounds = geometryBounds(geometry);
  const candidates = bounds
    ? cells.filter(
        (c) =>
          c.lat >= bounds[0][0] &&
          c.lat <= bounds[1][0] &&
          c.lon >= bounds[0][1] &&
          c.lon <= bounds[1][1]
      )
    : cells;
  return candidates
    .filter((c) => pointInPolygon({ lat: c.lat, lon: c.lon }, geometry))
    .map((c) => ({
      code: String(c.id),
      name: `Celda ROCIO #${c.id}`,
      lat: c.lat,
      lon: c.lon,
      altitudeM: c.heightM,
      distanceKm: haversineDistanceKm(referencePoint, c),
    }))
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

// ---------------------------------------------------------------------------
// Deterministic mock — dev-only, see the module header comment.
// ---------------------------------------------------------------------------

/** Tiny string hash (FNV-1a) -> a 32-bit seed, so the SAME (cellId, date) always mocks the SAME value across re-renders/reloads within a session, instead of flickering randomly every time it's recomputed. */
function hashSeed(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 — a small, fast, deterministic PRNG from a 32-bit seed (public domain algorithm, no dependency needed for something this simple). */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A plausible-looking (not real) daily precipitation value in mm, for one
 * grid cell on one date — mock data only, see the module header comment.
 * Mostly dry days with occasional rain, wetter in autumn/winter than summer
 * (Spain's real broad seasonal pattern, exaggerated/simplified on purpose —
 * this only needs to "look sane" for testing the fetch/aggregate/apply
 * pipeline, not to model real climatology).
 */
export function mockDailyMm(cellId: number, isoDate: string): number {
  const rand = mulberry32(hashSeed(`${cellId}_${isoDate}`));
  const month = Number(isoDate.slice(5, 7));
  // Roughly: Oct-Mar wetter (dry-day chance ~0.55), Apr-May/Sep shoulder
  // (~0.70), Jun-Aug dry season (~0.85).
  const dryChance =
    month <= 3 || month >= 10 ? 0.55 : month === 4 || month === 5 || month === 9 ? 0.7 : 0.85;
  if (rand() < dryChance) return 0;
  // Exponential-ish tail (mean ~6mm on a wet day, rare heavier events),
  // clamped to a plausible daily max.
  const magnitude = -Math.log(1 - rand()) * 6;
  return Math.min(120, Math.round(magnitude * 10) / 10);
}

/** Rough monthly mean daily minimum/maximum (°C) for mainland Spain, index 0 = January — exaggerated/simplified on purpose, same "only needs to look sane" spirit as `mockDailyMm`, not real climatology. */
const MONTHLY_TMIN_C = [2, 3, 5, 7, 11, 15, 18, 18, 15, 11, 6, 3];
const MONTHLY_TMAX_C = [12, 14, 17, 19, 23, 29, 33, 33, 28, 21, 15, 12];

/** Mock daily minimum temperature (°C), same determinism contract as `mockDailyMm`. */
export function mockDailyTmin(cellId: number, isoDate: string): number {
  const rand = mulberry32(hashSeed(`tmin_${cellId}_${isoDate}`));
  const month = Number(isoDate.slice(5, 7));
  const base = MONTHLY_TMIN_C[month - 1];
  return Math.round((base + (rand() - 0.5) * 6) * 10) / 10;
}

/** Mock daily maximum temperature (°C) — always at least 2°C above that same (cellId, date)'s `mockDailyTmin`, so `buildRocioEtoSeries`'s `tmax-tmin` never goes negative. */
export function mockDailyTmax(cellId: number, isoDate: string): number {
  const rand = mulberry32(hashSeed(`tmax_${cellId}_${isoDate}`));
  const month = Number(isoDate.slice(5, 7));
  const base = MONTHLY_TMAX_C[month - 1];
  const tmin = mockDailyTmin(cellId, isoDate);
  const tmax = Math.max(base + (rand() - 0.5) * 6, tmin + 2);
  return Math.round(tmax * 10) / 10;
}

// ---------------------------------------------------------------------------
// Value fetching — real (R2-backed Function) in production, mocked in dev.
// ---------------------------------------------------------------------------

interface RocioCellsResponse {
  start: string;
  end: string;
  cells: Record<
    string,
    { prec: (number | null)[]; tmin: (number | null)[]; tmax: (number | null)[] }
  >;
}

/**
 * Downloads (or, in dev, mocks) daily precipitation + tmin/tmax for
 * `cellIds` over `range`, returning the SAME shape `fetchDailyValues`
 * (aemet.ts) does — `byStationByDate`/`failedWindows` — so it plugs directly
 * into `buildRocioSeries`/`buildRocioEtoSeries` (or, for backward
 * compatibility with a plain per-cell reading, `buildSeriesFromStations`)
 * with zero changes needed elsewhere: those functions only ever cared about
 * station-shaped codes and a date->record map, never anything
 * AEMET-specific.
 *
 * Deliberately does NOT window/paginate/rate-limit the request the way
 * `fetchDailyValues` does for AEMET — that machinery exists solely because
 * AEMET's own API caps a single request's date range (STATION_WINDOW_DAYS)
 * and has a real shared rate limit; R2 has neither, a multi-decade range is
 * one request. `prec`/`tmax`/`tmin` are all real values now (`null`/no grid
 * coverage that day surfaces as `NaN`, a genuine gap, same as
 * `parseAemetNumber`'s contract) — `tmed` stays `NaN` always: ROCIO's raw
 * grid has no mean-temperature product of its own, `buildRocioEtoSeries`
 * approximates it as the midpoint of the aggregated tmax/tmin instead of
 * reading it here.
 */
export async function fetchRocioValues(
  range: DateRange,
  cellIds: string[],
  opts: { signal?: AbortSignal } = {}
): Promise<FetchDailyValuesResult> {
  const byStationByDate = new Map<string, Map<string, AemetDailyRecord>>();
  const dates: string[] = [];
  for (let d = range.start; d <= range.end; d = addDaysIso(d, 1)) dates.push(d);

  if (import.meta.env.DEV) {
    for (const cellId of cellIds) {
      const byDate = new Map<string, AemetDailyRecord>();
      for (const date of dates) {
        byDate.set(date, {
          prec: mockDailyMm(Number(cellId), date),
          tmed: NaN,
          tmax: mockDailyTmax(Number(cellId), date),
          tmin: mockDailyTmin(Number(cellId), date),
        });
      }
      byStationByDate.set(cellId, byDate);
    }
    return { byStationByDate, failedWindows: [] };
  }

  try {
    const url = `/api/rocio/cells?ids=${cellIds.map(encodeURIComponent).join(",")}&start=${range.start}&end=${range.end}`;
    const res = await fetch(url, { signal: withTimeout(opts.signal) });
    if (!res.ok) throw await translatableFetchError(res);
    const body = (await res.json()) as RocioCellsResponse;
    const startIdx = daysBetweenIso(body.start, range.start);
    for (const cellId of cellIds) {
      const cellData = body.cells[cellId];
      const byDate = new Map<string, AemetDailyRecord>();
      dates.forEach((date, i) => {
        const idx = startIdx + i;
        const prec = cellData?.prec?.[idx];
        const tmax = cellData?.tmax?.[idx];
        const tmin = cellData?.tmin?.[idx];
        byDate.set(date, {
          prec: prec === null || prec === undefined ? NaN : prec,
          tmed: NaN,
          tmax: tmax === null || tmax === undefined ? NaN : tmax,
          tmin: tmin === null || tmin === undefined ? NaN : tmin,
        });
      });
      byStationByDate.set(cellId, byDate);
    }
    return { byStationByDate, failedWindows: [] };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    // Same gap found by deep-review (2026-08-29) as aemet.ts's own
    // fetchWindowWithRetry: a translatableFetchError, network failure,
    // invalid JSON, or an unexpected response shape all used to vanish
    // here — the caller only ever sees this range added to failedWindows.
    console.warn(
      `[rocio] fetch for ${range.start}..${range.end} (${cellIds.length} cell(s)) failed:`,
      err
    );
    return { byStationByDate, failedWindows: [range] };
  }
}

/**
 * ROCIO equivalent of aemet.ts's `fetchDailyValuesForWindows` — re-runs an
 * explicit list of `windows` and merges the results, so `aemetJobStore.ts`'s
 * `retryFailedWindows` can re-download just a ROCIO job's failed window(s)
 * instead of the whole range again. In practice `windows` here is always the
 * single full range `fetchRocioValues` itself reports on failure (see its
 * own comment: ROCIO never splits into multiple windows to begin with), but
 * this stays general — merging any number of windows sequentially, no
 * lanes/pacing needed since R2 has neither AEMET's rate limit nor its
 * per-request date-range cap (same reasoning as `fetchRocioValues` itself
 * not windowing/pacing).
 */
export async function fetchRocioValuesForWindows(
  windows: DateRange[],
  cellIds: string[],
  opts: { onProgress?: (done: number, total: number) => void; signal?: AbortSignal } = {}
): Promise<FetchDailyValuesResult> {
  const byStationByDate = new Map<string, Map<string, AemetDailyRecord>>();
  const failedWindows: DateRange[] = [];
  let done = 0;
  for (const w of windows) {
    const result = await fetchRocioValues(w, cellIds, { signal: opts.signal });
    if (result.failedWindows.length > 0) {
      failedWindows.push(...result.failedWindows);
    } else {
      for (const [cellId, byDate] of result.byStationByDate) {
        let existing = byStationByDate.get(cellId);
        if (!existing) {
          existing = new Map();
          byStationByDate.set(cellId, existing);
        }
        for (const [date, record] of byDate) existing.set(date, record);
      }
    }
    done++;
    opts.onProgress?.(done, windows.length);
  }
  return { byStationByDate, failedWindows };
}

// ---------------------------------------------------------------------------
// Cell aggregation — mean/min/max instead of IDW-style distance blending, see
// the module header comment on why. Used by aemetJobStore.ts's applyResult/
// useAemetFetch.ts's live report, and aemetRetry.ts's cold retry, for the
// "rocio" method — never by any station-based method.
// ---------------------------------------------------------------------------

function aggregateNumbers(values: number[], op: "mean" | "min" | "max"): number {
  if (values.length === 0) return NaN;
  if (op === "mean") return values.reduce((a, b) => a + b, 0) / values.length;
  return op === "min" ? Math.min(...values) : Math.max(...values);
}

/**
 * ROCIO's own cell-aggregation for one variable across every candidate cell
 * (all of them inside the sub-basin's polygon, see `cellsInPolygon`) — a day
 * only counts as covered if AT LEAST ONE cell has a real value that day
 * (ROCIO is already a complete grid, so an all-missing day should only
 * happen outside a variable's own manifest date coverage).
 */
export function buildRocioSeries(
  cellIds: string[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  variable: "prec" | "tmin" | "tmax",
  op: "mean" | "min" | "max"
): BuiltSeries {
  const sourceByDate = new Map<string, string | null>();
  const values = dates.map((date) => {
    const dayValues: number[] = [];
    for (const cellId of cellIds) {
      const v = byStationByDate.get(cellId)?.get(date)?.[variable];
      if (v !== undefined && !Number.isNaN(v)) dayValues.push(v);
    }
    const result = aggregateNumbers(dayValues, op);
    sourceByDate.set(date, Number.isNaN(result) ? null : ROCIO_SOURCE_LABEL);
    return result;
  });
  return { values, sourceByDate };
}

/**
 * ROCIO's ETP — same Hargreaves formula as everywhere else in the app
 * (aemet.ts's `hargreavesEto`), fed by the grid's own MAX(tmax)/MIN(tmin)
 * across candidate cells instead of a station reading. ROCIO's raw grid has
 * no tmed of its own (see the module header comment) — tmed is approximated
 * as the midpoint of tmax/tmin, the standard estimate when only the daily
 * extremes are available. A day only counts as covered if at least one cell
 * has a real tmax AND at least one has a real tmin that day (not necessarily
 * the SAME cell — each extreme is independently the most extreme reading
 * anywhere in the sub-basin that day).
 */
export function buildRocioEtoSeries(
  cellIds: string[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  latDeg: number
): BuiltSeries {
  const sourceByDate = new Map<string, string | null>();
  const values = dates.map((date) => {
    const tmaxValues: number[] = [];
    const tminValues: number[] = [];
    for (const cellId of cellIds) {
      const record = byStationByDate.get(cellId)?.get(date);
      if (!record) continue;
      if (!Number.isNaN(record.tmax)) tmaxValues.push(record.tmax);
      if (!Number.isNaN(record.tmin)) tminValues.push(record.tmin);
    }
    if (tmaxValues.length === 0 || tminValues.length === 0) {
      sourceByDate.set(date, null);
      return NaN;
    }
    const tmax = Math.max(...tmaxValues);
    const tmin = Math.min(...tminValues);
    const tmed = (tmax + tmin) / 2;
    sourceByDate.set(date, ROCIO_SOURCE_LABEL);
    return hargreavesEto(tmax, tmin, tmed, latDeg, dayOfYear(date));
  });
  return { values, sourceByDate };
}
